import { apiClient } from '../utils/apiClient';

export interface QuoteItem {
  resource_type: 'DESK' | 'MEETING_ROOM';
  resource_ids: string[];
  booking_type: 'daily' | 'monthly' | 'hourly';
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
}

export interface QuoteItemResponse extends QuoteItem {
  base_price: string;
  cgst_amount: string;
  sgst_amount: string;
  total_price: string;
}

export interface QuoteRequest {
  items: QuoteItem[];
}

export interface QuoteResponse {
  items: QuoteItemResponse[];
  base_price: string;
  cgst_amount: string;
  sgst_amount: string;
  total_tax: string;
  total_price: string;
  currency: string;
}

export interface QuoteParams {
  // Desk parameters (optional)
  deskCodes?: string[];
  deskBookingType?: 'daily' | 'monthly';
  deskStartDate?: string;
  deskStartTime?: string;
  deskEndDate?: string;
  deskEndTime?: string;
  
  // Meeting room parameters (optional)
  meetingRoomId?: string;
  meetingRoomStartDate?: string;
  meetingRoomStartTime?: string;
  meetingRoomEndDate?: string;
  meetingRoomEndTime?: string;
}

export class QuoteService {
  static async getQuote(params: QuoteParams): Promise<QuoteResponse> {
    const items: QuoteItem[] = [];
    
    // Add desk item if desk parameters are provided
    if (params.deskCodes && params.deskCodes.length > 0) {
      items.push({
        resource_type: 'DESK',
        resource_ids: params.deskCodes,
        booking_type: params.deskBookingType || 'daily',
        start_date: params.deskStartDate!,
        start_time: params.deskStartTime!,
        end_date: params.deskEndDate!,
        end_time: params.deskEndTime!
      });
    }
    
    // Add meeting room item if meeting room parameters are provided
    if (params.meetingRoomId) {
      items.push({
        resource_type: 'MEETING_ROOM',
        resource_ids: [params.meetingRoomId],
        booking_type: 'hourly',
        start_date: params.meetingRoomStartDate!,
        start_time: params.meetingRoomStartTime!,
        end_date: params.meetingRoomEndDate!,
        end_time: params.meetingRoomEndTime!
      });
    }
    
    if (items.length === 0) {
      throw new Error('No items to quote - provide either desk or meeting room parameters');
    }
    
    const requestBody: QuoteRequest = { items };
    
    try {
      const response = await apiClient.post('/api/booking-orders/quote', requestBody);
      
      const quoteData = response?.data;
      
      if (!quoteData) {
        throw new Error('No quote data received from API');
      }
      
      // Validate required fields
      if (!quoteData.total_price) {
        throw new Error('Invalid total_price in response');
      }
      
      return quoteData;
    } catch (error) {
      console.error('Error fetching quote:', error);
      throw error;
    }
  }

  // Helper method to format price for display
  static formatPrice(amount: string | number, currency: string = 'INR'): string {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numAmount);
  }

  // Helper method to get desk codes from quote
  static getDeskCodes(quote: QuoteResponse): string[] {
    const deskItem = quote.items.find(item => item.resource_type === 'DESK');
    return deskItem?.resource_ids || [];
  }

  // Helper method to get meeting room from quote
  static getMeetingRoom(quote: QuoteResponse): string | null {
    const meetingRoomItem = quote.items.find(item => item.resource_type === 'MEETING_ROOM');
    return meetingRoomItem?.resource_ids[0] || null;
  }

  // Helper method to calculate price breakdown
  static getPriceBreakdown(quote: QuoteResponse) {
    return {
      basePrice: parseFloat(quote.base_price),
      cgst: parseFloat(quote.cgst_amount),
      sgst: parseFloat(quote.sgst_amount),
      totalTax: parseFloat(quote.total_tax),
      totalPrice: parseFloat(quote.total_price),
      currency: quote.currency
    };
  }

  // Helper method to get item breakdown
  static getItemBreakdown(quote: QuoteResponse) {
    return quote.items.map(item => ({
      resourceType: item.resource_type,
      resourceIds: item.resource_ids,
      bookingType: item.booking_type,
      basePrice: parseFloat(item.base_price),
      cgst: parseFloat(item.cgst_amount),
      sgst: parseFloat(item.sgst_amount),
      totalPrice: parseFloat(item.total_price)
    }));
  }
}